/* eslint-disable max-lines-per-function */
import { filter, first, get, identity, map, uniq, uniqBy } from "lodash";
import React from "react";
import { Grid, Button } from "@material-ui/core";
import { useParams } from "@reach/router";
import RemoveUserModal from "pages/clients/interests/list/remove-user-modal";
import { PossibleWaitlistUpdateRemovalReasons } from "components/structured-reasons";
import { Interest } from "../../types";
import { FormProps } from "../types";
import { interestIsRemoved } from "../utils";
import styles from "./update.module.css";
import AllRemovedBanner from "./all-removed";

const fullName = (interest: Interest | undefined): React.ReactNode =>
  (interest ? (
    [interest.householdMember0FirstName, interest.householdMember0LastName]
      .filter(identity)
      .join(" ")) : null);

const humanWaitTime = (waitTime?: string): string => {
  switch (waitTime) {
    case "HALF_YEAR_WAITLIST":
      return "Under 6 months";
    case "UNDER_YEAR_WAITLIST":
      return "Under 1 year";
    case "UNDER_TWO_YEARS_WAITLIST":
      return "1-2 years";
    case "UNDER_THREE_YEARS_WAITLIST":
      return "2-3 years";
    case "UNDER_FIVE_YEARS_WAITLIST":
      return "4-5 years";
    case "OVER_FIVE_YEARS_WAITLIST":
      return "5+ years";
    case "OVER_DECADE_WAITLIST":
      return "10+ years";
    default:
      return "Multi-year wait";
  }
};
interface activeListItem {
  id: string,
  name: string | null,
  city: string,
  state: string,
  status: string | undefined,
}

const ChooseUpdateForm: React.FC<FormProps> = (props) => {
  const { code } = useParams();
  const emails = uniq(filter(map(map(props.data.interests, "interestList"), "email")));
  const getActiveLists = () => uniqBy(
    props.data.interests
      .map((i) => ({
        id: i.id ?? "",
        name: i.interestList.name,
        city: get(i, "interestList.address.city", "") as string,
        state: get(i, "interestList.address.state", "") as string,
        status: i.status.name,
        waitTime: i.interestList.waitTime,
      })),
    (x) => x.name,
  );

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [interestListName, setInterestListName] = React.useState("");
  const [interestId, setInterestId] = React.useState("");
  const [activeLists] = React.useState(() => getActiveLists());


  const handleOpenRemoveDialog = (list: activeListItem) => {
    setInterestListName(list.name ?? "");
    setInterestId(list.id);
    setIsModalOpen(true);
  };

  const handleCloseRemoveDialog = (isRemoved: boolean) => {
    setIsModalOpen(false);

    if (isRemoved) {
      props.setRemoved(interestId);
    }
  };

  return (
    <div>
      <h3>Hi {fullName(first(props.data.interests))}.
      Here are the current lists you&apos;re on:</h3>

      <Grid container className={styles.propertiesContainer}>
        {activeLists.map((list, i) => (
          <Grid item container className={styles.propertyRow} justifyContent="space-between" key={i}>
            <Grid item xs={12} sm={12} className={styles.propertyTitle}>
              <b>{list.name} {list.city && list.state && `(${list.city}, ${list.state})`}</b>
            </Grid>
            <Grid item xs={12} sm={10} className={styles.propertyTitle}>
              {humanWaitTime(list.waitTime)} estimated wait time
            </Grid>
            {!interestIsRemoved(list.id, list.status, props.removedInterests) &&
              (<Grid item xs={12} sm={2}>
                <Button variant="outlined" onClick={() => handleOpenRemoveDialog(list)} fullWidth>Remove</Button>
              </Grid>)
            }
            {interestIsRemoved(list.id, list.status, props.removedInterests) &&
              (<Grid item xs={12} sm={2} className={styles.propertyRemoved}>
                <b>Removed</b>
              </Grid>)}
          </Grid>
        ))}
        {(activeLists.every(
          list => interestIsRemoved(list.id, list.status, props.removedInterests),
        )) &&
          <Grid container className={styles.removedBanner} item xs={12} spacing={1}>
            <AllRemovedBanner />
          </Grid>
        }
      </Grid>

      <RemoveUserModal
        isOpen={isModalOpen}
        onRequestClose={handleCloseRemoveDialog}
        interestListName={interestListName || ""}
        interestId={interestId}
        removalReasons={PossibleWaitlistUpdateRemovalReasons}
        waitlistCode={code}
      />

      {emails.length > 0 && (
        <p className="margin-top-m">
          If your name is misspelled, please contact the property manager(s) at{" "}
          {emails
            .map<React.ReactNode>((email) => <a key={email} href={`mailto:${email}`}>{email}</a>)
            .reduce((result, element) => [result, ", ", element])}
        </p>
      )}
    </div>
  );
};

export default ChooseUpdateForm;
